import url from 'url';
import querystring from 'querystring';

import { Optional } from 'chaika-common-types';
import {
    UserData,
    AuthUserData,
    CreateTokenData,
    TokenValidationResponse,
    PasswordRecoveryData,
} from 'chaika-frontend-types/frontend-cms';

import { axios, AxiosResponse } from '@lib/axios';
import { Locale } from 'chaika-enums';

const BASE_URL = '/api/auth';

export class AuthApi {
    public static logout(): void {
        const { protocol, host } = url.parse(window.location.href);
        window.location.href = url.format({
            protocol,
            host,
            pathname: `${BASE_URL}/logout`,
        });
    }

    public static async redirectToLogin(): Promise<void> {
        const { protocol, host } = url.parse(window.location.href);
        window.location.href = url.format({
            protocol,
            host,
            pathname: 'login',
        });
    }

    public static async loginAsUser(email: string): Promise<void> {
        try {
            await axios.post(`${BASE_URL}/login-as`, { email });

            const { protocol, host } = url.parse(window.location.href);
            window.location.href = url.format({
                protocol,
                host,
                pathname: 'patient',
            });
        } catch (e: any) {
            alert(e);
        }
    }

    public static async reserveLogin(email: string, password: string): Promise<void> {
        const res = await axios.post<string>(`${BASE_URL}/login`, { user: { email, password } });
        const { protocol, host } = url.parse(window.location.href);
        const decodeUrl = decodeURIComponent(res.data);
        const searchIndex = decodeUrl.indexOf('?');

        const urlTo = url.format({
            protocol,
            host,
            pathname: searchIndex !== -1 ? decodeUrl.slice(0, searchIndex) : decodeUrl,
        });
        window.location.href = `${urlTo}${searchIndex !== -1 ? `?${decodeUrl.slice(searchIndex + 1)}` : ''}`;
    }

    public static async createToken(token: CreateTokenData): Promise<void> {
        await axios.post(`${BASE_URL}/token`, { token });
    }

    public static async deactivateToken(token: string): Promise<void> {
        await axios.delete(`${BASE_URL}/token/${token}`);
    }

    public static async recoveryPassword(token: string, recovery: PasswordRecoveryData): Promise<void> {
        await axios.put(`${BASE_URL}/token/${token}/password`, { recovery });
    }

    public static async getTokenValidation(token: string): Promise<TokenValidationResponse> {
        const res = await axios.get<TokenValidationResponse>(`${BASE_URL}/token/${token}/validation`);

        return res.data;
    }

    // Save lang in to redis session
    public static async setUserLang(userLang: Locale): Promise<void> {
        await axios.put(`${BASE_URL}/user/locale`, { userLang });
    }

    public static async setFromUrl(from: string): Promise<void> {
        await axios.put(`${BASE_URL}/from`, { from });
    }

    public static async getAuthorizedUser(): Promise<AuthUserData> {
        const res: AxiosResponse<{ user: AuthUserData }> = await axios.get(`${BASE_URL}/user`, {
            withCredentials: true,
        });

        return res.data.user;
    }

    public static async getTimeLeft(): Promise<number> {
        const res: AxiosResponse<{ timeLeft: number }> = await axios.get(`${BASE_URL}/time-left`);

        return res.data.timeLeft;
    }

    public static async testLogin(email: string): Promise<Optional<UserData>> {
        return axios.post(`${BASE_URL}/login`, { email });
    }

    public static async legacyLogin(email: string): Promise<Optional<UserData>> {
        let user: Optional<UserData>;

        try {
            user = await axios.post(`${BASE_URL}/login`, { email });

            const from = querystring.parse(window.location.search).from as string;
            const { protocol, host } = url.parse(window.location.href);
            window.location.href = url.format({
                protocol,
                host,
                pathname: from,
            });
        } catch (e: any) {
            alert(e);
        }

        return user;
    }
}

export { BASE_URL as AUTH_API_BASE_URL };
